import { create } from 'zustand';
import {
  EMPTY_MODEL,
  cancelPending,
  backgroundPending,
  chooseReplace,
  closeWindow,
  findWindow,
  focusWindow,
  isVisible,
  minimizeAllWindows,
  minimizeWindow,
  moveWindow,
  openBeside,
  openWindow,
  restoreWindow,
  retargetWindow,
  setCapacity,
  setWindowTitle,
  toggleWindow,
  type OpenWindowInput,
  type WindowsModel,
} from './window-rules';

/**
 * FRONT-3146: the one holder of the open-window model (rules live in
 * `window-rules.ts`). The three open stores (`chat-open-store`,
 * `coding-open-store`, `pro-builder-open-store`) keep their public APIs and
 * route through here, so every existing doorway gets the same behaviour.
 *
 * `dismissHandlers`: a window whose owner is a page-local mount (the Files
 * page's document viewer, ...) registers what "the person closed this from the
 * window bar / chose Cancel" means for that owner — usually its own `onClose`.
 * It is invoked only for a removal the OWNER did not cause: an owner that
 * unmounts itself calls `unregisterWindow`, which drops the handler first, so
 * nothing loops.
 */
interface WindowStoreState extends WindowsModel {
  open: (input: OpenWindowInput, onDismiss?: () => void) => void;
  /** Open docked beside another window, minimizing the rest (see `openBeside`). */
  openBeside: (input: OpenWindowInput, besideId?: string | null) => void;
  minimize: (id: string) => void;
  /** Put every window in the bar — a sidebar page was chosen (see `minimizeAllWindows`). */
  minimizeAll: () => void;
  restore: (id: string) => void;
  toggle: (id: string) => void;
  close: (id: string) => void;
  /** The owner went away on its own — remove the entry without calling its dismiss handler. */
  unregister: (id: string) => void;
  chooseReplace: (replaceId: string) => void;
  cancelPending: () => void;
  /** Place the waiting window minimized ("start it in the background"). */
  backgroundPending: () => void;
  focus: (id: string) => void;
  /** The content area was resized: how many windows fit side by side (see `setCapacity`). */
  setCapacity: (capacity: number) => void;
  setTitle: (id: string, title: string) => void;
  /** A tab was dragged: reorder tabs and columns together (see `moveWindow`). */
  move: (id: string, targetId: string, side: 'before' | 'after') => void;
  retarget: (id: string, next: { id: string; ref: string | null; title: string }) => void;
}

const dismissHandlers = new Map<string, () => void>();

function runDismissedHandlers(before: WindowsModel, after: WindowsModel): void {
  for (const w of before.windows) {
    if (!findWindow(after, w.id)) {
      const handler = dismissHandlers.get(w.id);
      dismissHandlers.delete(w.id);
      handler?.();
    }
  }
}

export const useWindowStore = create<WindowStoreState>((set, get) => {
  /** Applies a pure transition and tells owners of windows it removed. */
  const apply = (transition: (m: WindowsModel) => WindowsModel, silent = false): void => {
    const before = get();
    const after = transition({
      windows: before.windows,
      focusedId: before.focusedId,
      pendingId: before.pendingId,
      capacity: before.capacity,
      squeezedIds: before.squeezedIds,
    });
    set({
      windows: after.windows,
      focusedId: after.focusedId,
      pendingId: after.pendingId,
      capacity: after.capacity,
      squeezedIds: after.squeezedIds,
    });
    if (!silent) runDismissedHandlers(before, after);
  };
  return {
    ...EMPTY_MODEL,
    open: (input, onDismiss) => {
      if (onDismiss) dismissHandlers.set(input.id, onDismiss);
      apply((m) => openWindow(m, input));
    },
    openBeside: (input, besideId) => apply((m) => openBeside(m, input, besideId)),
    minimize: (id) => apply((m) => minimizeWindow(m, id)),
    minimizeAll: () => apply(minimizeAllWindows),
    restore: (id) => apply((m) => restoreWindow(m, id)),
    toggle: (id) => apply((m) => toggleWindow(m, id)),
    close: (id) => apply((m) => closeWindow(m, id)),
    unregister: (id) => {
      dismissHandlers.delete(id);
      apply((m) => closeWindow(m, id), true);
    },
    chooseReplace: (replaceId) => apply((m) => chooseReplace(m, replaceId)),
    cancelPending: () => apply((m) => cancelPending(m)),
    backgroundPending: () => apply((m) => backgroundPending(m)),
    focus: (id) => apply((m) => focusWindow(m, id)),
    setCapacity: (capacity) => apply((m) => setCapacity(m, capacity)),
    setTitle: (id, title) => apply((m) => setWindowTitle(m, id, title)),
    move: (id, targetId, side) => apply((m) => moveWindow(m, id, targetId, side)),
    retarget: (id, next) => {
      const handler = dismissHandlers.get(id);
      apply((m) => retargetWindow(m, id, next), true);
      if (handler && next.id !== id) {
        dismissHandlers.delete(id);
        dismissHandlers.set(next.id, handler);
      }
    },
  };
});

/** Non-hook reads/writes for code outside a component tree (the open stores). */
export const windowActions = {
  open: (input: OpenWindowInput, onDismiss?: () => void) => useWindowStore.getState().open(input, onDismiss),
  close: (id: string) => useWindowStore.getState().close(id),
  isVisible: (id: string) => isVisible(useWindowStore.getState(), id),
};

/** Test helper: back to no windows and no handlers. */
export function resetWindowStore(): void {
  dismissHandlers.clear();
  useWindowStore.setState({ ...EMPTY_MODEL, capacity: undefined, squeezedIds: undefined });
}
